import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, GitBranch, Award, ShieldCheck, X } from "lucide-react";

import { get } from "@/lib/api-client";
import { formatDateTime, formatCurrency } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EntryTreeMini } from "./components/EntryTreeMini";
import type { ClubEntry, StageRecord, TreeNode } from "@/types/club";

// Helper function to convert stage number to "1st bonus", "2nd bonus"
function getOrdinalBonusName(n: number) {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return n + "st bonus";
  if (j === 2 && k !== 12) return n + "nd bonus";
  if (j === 3 && k !== 13) return n + "rd bonus";
  return n + "th bonus";
}

// 🛠️ MOCK REWARD AMOUNTS
const STAGE_REWARD_AMOUNTS: Record<number, number> = {
  1: 100,
  2: 200,
  3: 200,
  4: 200,
  5: 200,
  6: 200,
  7: 500,
};

export function EntryDetailPage() {
  const { entryId } = useParams();
  const nav = useNavigate();

  const entry = useQuery({
    queryKey: ["entry", entryId],
    queryFn: () => get<ClubEntry>(`/club/entries/entries/${entryId}`),
    enabled: !!entryId,
  });

  const stages = useQuery({
    queryKey: ["entry-stages", entryId],
    queryFn: async () => {
      try {
        return await get<StageRecord[]>(`/stages/entries/${entryId}/stages`);
      } catch (err: any) {
        if (err.message === "Not found." || err?.response?.status === 404) return [];
        throw err;
      }
    },
    enabled: !!entryId,
  });
  
  const node = useQuery({
    queryKey: ["entry-tree", entryId],
    queryFn: async () => {
      try {
        return await get<TreeNode>(`/placement/tree/${entryId}`);
      } catch (err: any) {
        if (err.message === "Not found." || err?.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!entryId,
    retry: false,
  });

  if (entry.isLoading || !entry.data) return <Skeleton className="h-96 w-full rounded-xl" />;
  const e = entry.data;

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-10">
      <button onClick={() => nav(-1)} className="text-sm font-semibold text-surface-500 hover:text-surface-900 inline-flex items-center gap-1 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to My Clubs
      </button>

      {/* Hero Header */}
      <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-sm border border-surface-200 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-surface-500 font-bold mb-1">Club Details</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-surface-900 font-mono tracking-tight flex items-center flex-wrap gap-2">
            {e.entry_id}
            <span className="text-xs sm:text-sm font-semibold bg-brand-50 text-brand-700 px-2 py-1 rounded-lg capitalize tracking-normal border border-brand-200">
              {e.creation_method || "Unknown"}
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-3 bg-surface-50 p-2.5 rounded-xl border border-surface-200">
          <div className="text-right">
            <div className="text-[10px] uppercase font-bold text-surface-400">Club Status</div>
            <StatusPill status={e.status} />
          </div>
          <div className="w-px h-8 bg-surface-200 mx-1" />
          <div className="text-left">
            <div className="text-[10px] uppercase font-bold text-surface-400">Placement</div>
            <StatusPill status={e.placement_status} />
          </div>
        </div>
      </div>

      {/* Success Banner */}
      {e.status === "completed" && (
        <div className="bg-success-50 border border-success-200 p-4 sm:p-5 rounded-2xl flex items-center justify-between shadow-sm animate-fade-in">
          <div className="flex items-start sm:items-center gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-success-100 flex items-center justify-center text-success-600 shadow-sm shrink-0">
              <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-success-900 leading-tight">Congratulations! This Club is Fully Completed.</h2>
              <p className="text-xs sm:text-sm text-success-700 mt-1">All 7 stages have been successfully finished and bonuses rewarded.</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-3 items-start">
        
        {/* 🌟 LEFT: STAGE PROGRESS TABLE (Mobile Responsive!) */}
        <Card className="xl:col-span-2 border-0 shadow-elevate-1 ring-1 ring-surface-200/50 rounded-2xl overflow-hidden bg-white h-fit">
          <CardHeader className="bg-surface-50/50 border-b border-surface-100 flex justify-between items-center px-4 sm:px-6">
            <CardTitle>Bonus Progress</CardTitle>
            <div className="flex items-center gap-1.5 text-xs font-bold text-surface-500 bg-white px-2 py-1 rounded-md border border-surface-200">
              <Award className="h-4 w-4 text-warning-500" /> {stages.data?.length ?? 0} Stages
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {stages.isLoading ? (
              <div className="p-6 space-y-4">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : stages.data && stages.data.length > 0 ? (
              
              /* 🛠️ FIXED: No horizontal scrolling, fits in one view */
              <div className="w-full">
                <table className="w-full text-left border-collapse table-fixed">
                  <thead className="bg-[#3FA9F5] text-white">
                    <tr>
                      <th className="px-2 sm:px-4 py-3 font-medium text-[13px] sm:text-[15px] w-[28%]">Bonus</th>
                      <th className="px-1 sm:px-4 py-3 font-medium text-[13px] sm:text-[15px] w-[20%] text-center">Amount</th>
                      <th className="px-1 sm:px-4 py-3 font-medium text-[13px] sm:text-[15px] w-[27%] text-center">Status</th>
                      <th className="px-2 sm:px-4 py-3 font-medium text-[13px] sm:text-[15px] w-[25%] text-right">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-100 bg-white">
                    {stages.data.map((s) => {
                      const isCompleted = s.status === "completed";
                      const isActive = s.status === "active";
                      
                      // 🛠️ Date Formatting: 21-08-2025 format
                      const formattedDate = s.completed_at 
                        ? new Date(s.completed_at).toLocaleDateString("en-GB").replace(/\//g, "-") 
                        : "";

                      return (
                        <tr key={s.stage_number} className="hover:bg-surface-50 transition-colors">
                          {/* Bonus Name */}
                          <td className="px-2 sm:px-4 py-4 font-medium text-surface-900 text-[13px] sm:text-[15px] truncate">
                            {getOrdinalBonusName(s.stage_number)}
                          </td>
                          
                          {/* Amount */}
                          <td className="px-1 sm:px-4 py-4 text-surface-900 text-[13px] sm:text-[15px] text-center">
                            {STAGE_REWARD_AMOUNTS[s.stage_number] ?? 0}
                          </td>
                          
                          {/* Status Pill */}
                          <td className="px-1 sm:px-4 py-4 text-center">
                            {isCompleted ? (
                              <span className="bg-[#5CB85C] text-white text-[10px] sm:text-[11px] font-medium tracking-wide px-1.5 sm:px-3 py-1 sm:py-1.5 rounded shadow-sm whitespace-nowrap">
                                COMPLETED
                              </span>
                            ) : isActive ? (
                              <span className="bg-[#3FA9F5] text-white text-[10px] sm:text-[11px] font-medium tracking-wide px-1.5 sm:px-3 py-1 sm:py-1.5 rounded shadow-sm whitespace-nowrap">
                                PROCESSING
                              </span>
                            ) : (
                              <span className="bg-surface-200 text-surface-600 text-[10px] sm:text-[11px] font-medium tracking-wide px-1.5 sm:px-3 py-1 sm:py-1.5 rounded whitespace-nowrap">
                                LOCKED
                              </span>
                            )}
                          </td>
                          
                          {/* Date */}
                          <td className="px-2 sm:px-4 py-4 text-[12px] sm:text-[14px] text-surface-800 text-right whitespace-nowrap">
                            {formattedDate}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

            ) : (
              <div className="text-sm text-surface-500 py-16 text-center font-medium">
                Bonuses will initialize once the Club is placed.
              </div>
            )}
          </CardBody>
        </Card>

        {/* RIGHT: METADATA & PLACEMENT */}
        <div className="space-y-6">
          <Card className="rounded-2xl border-0 shadow-elevate-1 ring-1 ring-surface-200/50">
            <CardHeader className="bg-surface-50/50 border-b border-surface-100">
              <CardTitle>Placement Info</CardTitle>
            </CardHeader>
            <CardBody>
              {node.isLoading ? (
                <div className="text-sm text-surface-500 py-4 text-center">Loading tree...</div>
              ) : node.data ? (
                <EntryTreeMini node={node.data} />
              ) : (
                <div className="text-sm text-surface-500 py-10 text-center flex flex-col items-center">
                  <GitBranch className="w-10 h-10 text-surface-300 mb-3" />
                  <span className="font-semibold text-surface-700">Not placed yet.</span>
                  <p className="text-xs mt-1">Waiting in the queue.</p>
                </div>
              )}
            </CardBody>
          </Card>

          <Card className="rounded-2xl border-0 shadow-elevate-1 ring-1 ring-surface-200/50">
            <CardHeader className="bg-surface-50/50 border-b border-surface-100">
              <CardTitle>Metadata</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3.5 text-sm">
              <Row label="Created On" value={formatDateTime(e.created_at)} />
              <Row label="Last Updated" value={formatDateTime(e.updated_at)} />
              
              {e.source_order_id && (
                <Row label="Source Order" value={<span className="font-mono text-xs bg-surface-100 px-2 py-1 rounded">{e.source_order_id}</span>} />
              )}
              {e.source_completed_entry_id && (
                <Row label="Re-entry From" value={<span className="font-mono text-xs bg-surface-100 px-2 py-1 rounded">{e.source_completed_entry_id}</span>} />
              )}
              
              <div className="divider my-2" />
              
              {e.correlation_id && (
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-surface-400">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-mono truncate">Corr ID: {e.correlation_id.slice(0, 12)}…</span>
                </div>
              )}
            </CardBody>
          </Card>
        </div>

      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-surface-500 font-medium">{label}</span>
      <span className="font-semibold text-surface-900 text-right">{value}</span>
    </div>
  );
}